import { GetGroupDiscount, getProducts, getProductsByDepartment } from 'lib/woocomerce';
import {
  GetGroupDiscountQuery,
  OrderEnum,
  ProductsOrderByEnum
} from 'lib/woocomerce/__generated__/graphql';
import { Product } from 'lib/woocomerce/types';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useStore } from 'src/context/store-context';
import { notifyError } from '../utils';

export type ProductsContextInput = {
  initialProducts: Product[];
};

export type ProductsContextState = {
  products: Product[];
  storeDiscount: number;
  hasDiscount: boolean;
  isFetchingProducts: boolean;
  setHasDiscount: Dispatch<SetStateAction<boolean>>;
  fetchProducts: () => void;
  fetchStoreDiscount: () => void;
};

const defaultProductsContextValue: ProductsContextState = {
  products: [],
  storeDiscount: 0,
  hasDiscount: false,
  isFetchingProducts: false,
  setHasDiscount: () => {},
  fetchProducts: () => {},
  fetchStoreDiscount: () => {}
};

const ProductsContext = createContext<ProductsContextState>(defaultProductsContextValue);

export const ProductsProvider = ({
  value,
  children
}: {
  value: ProductsContextInput;
  children: React.ReactNode;
}) => {
  const [{ walletAddress, selectedDepartment }] = useStore();
  const [products, setProducts] = useState<Product[]>(value.initialProducts || []);
  const [storeDiscount, setStoreDiscount] = useState<number>(
    defaultProductsContextValue.storeDiscount
  );
  const [hasDiscount, setHasDiscount] = useState<boolean>(defaultProductsContextValue.hasDiscount);
  const [isFetchingProducts, setIsFetchingProducts] = useState<boolean>(false);

  const prevWalletAddress = useRef<string | undefined>();

  const fetchProducts = useCallback(async () => {
    const address = walletAddress || '';
    const departmentSlug = selectedDepartment?.slug || '';
    const departmentId = selectedDepartment?.id || 'default-all';
    try {
      setIsFetchingProducts(true);
      if (departmentSlug && departmentId !== 'default-all') {
        const products = (await getProductsByDepartment({
          address,
          department: departmentSlug,
          first: 1000
        })) as Product[];
        setProducts(products);
      } else {
        const products = (await getProducts({
          address,
          first: 1000,
          orderby: [{ order: OrderEnum.Asc, field: ProductsOrderByEnum.MenuOrder }]
        })) as Product[];
        setProducts(products);
      }
    } catch (err) {
      notifyError(err);
    } finally {
      setIsFetchingProducts(false);
    }
  }, [walletAddress, selectedDepartment?.slug, selectedDepartment?.id]);

  const fetchStoreDiscount = useCallback(async () => {
    const address = walletAddress || '';
    prevWalletAddress.current = walletAddress;
    // Set store discount and allowlist status
    (async () => {
      try {
        const { discounts } = (await GetGroupDiscount(address)) as GetGroupDiscountQuery;
        setStoreDiscount(Number(discounts.nodes[0]?.groupDiscount.percentage || 0));
      } catch (err) {
        notifyError(err);
      }
    })();
  }, [walletAddress]);

  useEffect(() => {
    fetchStoreDiscount();
  }, [fetchStoreDiscount]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <ProductsContext.Provider
      value={{
        products,
        storeDiscount,
        hasDiscount,
        setHasDiscount,
        fetchProducts,
        fetchStoreDiscount,
        isFetchingProducts
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProductsContext = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProductsContext must be used within a ProductsProvider');
  }
  return context;
};
